import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import CompanyForm from "../components/CompanyForm/CompanyForm";
import Alert from "../components/Alert/Alert";
import MembersTable from "../components/Company/MembersTable";
import MemberFormModal from "../components/Company/MemberFormModal";
import { getDashboard } from "../api/boards";
import { updateCompany as apiUpdateCompany } from "../api/company";
import { getCompanyMembers, inviteMember, updateMemberRole, removeMember, cancelInvitation } from "../api/members";

const Company = () => {
  const { t } = useTranslation();
  const [company, setCompany] = useState(null);
  const [initialCompanyName, setInitialCompanyName] = useState("");
  const [initialCompanySlug, setInitialCompanySlug] = useState("");
  const [members, setMembers] = useState([]);
  const [alert, setAlert] = useState({ show: false, text: "", type: "" });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const navigate = useNavigate();

  const showAlert = (text, type) => setAlert({ show: true, text, type });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) { navigate("/login"); return; }
      const data = await getDashboard();
      const adminCompany = data.companies?.find(c => c.company_role === "admin");
      if (!adminCompany) { navigate("/dashboard"); return; }
      setCompany(adminCompany);
      setInitialCompanyName(adminCompany.name || "");
      setInitialCompanySlug(adminCompany.slug || "");
    } catch (err) {
      if (err.status === 401) { navigate("/login"); return; }
      console.error("Помилка:", err);
    }
  }, [navigate]);

  const fetchMembers = useCallback(async () => {
    try {
      const data = await getCompanyMembers();
      setMembers(data.members || []);
    } catch (err) {
      showAlert(t('company.load_error'), "error");
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  useEffect(() => {
    if (!alert.show) return;
    const id = setTimeout(() => setAlert({ show: false, text: "", type: "" }), 5000);
    return () => clearTimeout(id);
  }, [alert.show]);

  const handleSaveChanges = async (updatedName, updatedSlug) => {
    try {
      await apiUpdateCompany(company.slug, updatedName, updatedSlug);
      setCompany(prev => ({ ...prev, name: updatedName, slug: updatedSlug }));
      setInitialCompanyName(updatedName);
      setInitialCompanySlug(updatedSlug);
      showAlert(t('company.success_saved'), "success");
    } catch {
      showAlert(t('company.save_error'), "error");
    }
  };

  const handleResetChanges = () => {
    setInitialCompanyName(company.name);
    setInitialCompanySlug(company.slug);
  };

  const handleInvite = async (form) => {
    try {
      await inviteMember(form);
      showAlert(t('company.invite_success'), "success");
      setShowInviteModal(false);
      fetchMembers();
    } catch (err) {
      showAlert(err.message || t('company.invite_error'), "error");
    }
  };

  const handleEditSave = async (updatedMember) => {
    try {
      await updateMemberRole(updatedMember.id, updatedMember.role, updatedMember.full_name, updatedMember.email);
      showAlert(t('company.role_success'), "success");
      setEditMember(null);
      fetchMembers();
    } catch (err) {
      showAlert(err.message || t('company.role_error'), "error");
    }
  };

  const handleRemoveMember = async (member) => {
    try {
      if (member.status === "invited") {
        await cancelInvitation(member.invite_token);
      } else {
        await removeMember(member.id);
      }
      fetchMembers();
    } catch (err) {
      showAlert(err.message || t('company.delete_error'), "error");
    }
  };

  if (!company) return null;

  return (
    <div className="page-container" style={{ maxWidth: 800 }}>
      {alert.show && (
        <Alert text={alert.text} type={alert.type} onClose={() => setAlert({ show: false, text: "", type: "" })} />
      )}

      <CompanyForm
        initialCompanyName={initialCompanyName}
        initialCompanySlug={initialCompanySlug}
        onSave={handleSaveChanges}
        onReset={handleResetChanges}
      />

      <MembersTable
        members={members}
        onEdit={setEditMember}
        onRemove={handleRemoveMember}
        onInvite={() => setShowInviteModal(true)}
      />

      {editMember && (
        <MemberFormModal
          mode="edit"
          member={editMember}
          onClose={() => setEditMember(null)}
          onSubmit={handleEditSave}
        />
      )}

      {showInviteModal && (
        <MemberFormModal
          mode="invite"
          onClose={() => setShowInviteModal(false)}
          onSubmit={handleInvite}
        />
      )}
    </div>
  );
};

export default Company;
